/**
 * **Post-move spatial-relation recomputers**: pure `(state, move) → grounded steps` functions run
 * **after** a token moves, to recompute the positional relations that change when a unit moves.
 *
 * **Two lists, split by D15** (`event-engine-lifecycle`, see {@link MOVE_LISTENER_RECOMPUTERS}):
 * the rule listeners on `moved` (drag, jockey, grapple, Argonaut Shield, Hidden revealed), then the
 * settle pass that re-syncs derived state (Engaged, the grapple dominance sync).
 * `referee/refereeIntent.ts`'s `recomputeAfterMoveInto` runs both, in that order; `moved`'s first
 * rule (`referee/movement.ts`) and every displacement call it.
 *
 * Each recomputer is **pure** — it reads the frozen post-move state and *returns* `status_*` (or
 * other) steps; it never mutates. The referee lands each recomputer's steps as its own
 * `apply_resolved` entry under `spatial_<id>`, so every consequence is a reified, invertible
 * grounded step.
 *
 * **Determinism.** Each list runs in its fixed order (never registration/iteration order), and each
 * recomputer emits its steps in a **sorted** order (combatant ids ascending) so the recorded step
 * list is byte-identical across replay — undo/replay closure depends on it.
 *
 * **Category A only.** A recomputer fires-and-forgets: it never pauses resolution to ask
 * a human — that is a *reaction*, and it lives in the reaction registries.
 */

import {
  type Cell,
  type EncounterState,
  type Combatant,
  type ResolvedStep,
  sideOf,
  hasStatus,
  isActor,
  RODEO_RIDER_STATUS,
  RODEO_STATUS,
} from "@lancer-vtt/domain";
import { combatantRange } from "./effects/spatialAnswerer";
import { GRAPPLED_STATUS, grappleImmobilizedSyncSteps, statusRemoveStep } from "./grapple";
import { JOCKEYED_STATUS, JOCKEYING_STATUS, RIDDEN_STATUS, RIDING_STATUS, jockeyDetachSteps, rideDetachSteps } from "./jockey";
import { endRodeoSteps } from "./rodeo";
import { dragRecomputer } from "./dragRecompute";
import { revealExposedHiddenSteps } from "./hide";

/** The firing context handed to each post-move recomputer. */
export type MoveContext = {
  /** The authoritative state **after** the move has landed. */
  state: EncounterState;
  /** The combatant that moved (the move's subject). */
  moved: string;
  /**
   * `moved`'s anchor **before** the move was applied — the mover's pre-move cell, the `from` half of the strict-mirror
   * `from → to` displacement {@link "./dragRecompute".dragRecomputer} translates the
   * draggee by. **Optional and consumed by the drag alone** — every other recomputer
   * reads only the post-move `state` and ignores it. The move chain passes it whenever the move
   * has one; a teleport, a placement and a displacement inside a resolution have none, and the drag
   * treats that as "no step".
   */
  from?: Cell;
};

/**
 * One post-move spatial-relation recomputer: a pure function from the post-move state
 * to the grounded steps that bring one positional relation back in sync. `id` is the
 * stable identity the chain orders by (deterministic, replay-safe).
 */
export type SpatialRecomputer = {
  /** Stable id — the chain runs sorted/fixed by this, never by registration order. */
  readonly id: string;
  /** Pure: read the post-move state, return the steps that re-sync this relation. Never mutates. */
  recompute(ctx: MoveContext): ResolvedStep[];
};

/**
 * The canonical RAW **ENGAGED** status name (Lancer Core, "STATUSES"). Engaged is a
 * first-class Lancer *status*, so modelling it as a `StatusInstance` name is RAW-faithful
 * (not an implementation invention) — the movement rules then *consume* this status (the
 * Disengage suppression, the stop check) rather than re-deriving adjacency.
 */
export const ENGAGED_STATUS = "Engaged";

/**
 * RAW Engagement (Lancer Core, "ENGAGED"): a character is **Engaged** while it is
 * adjacent to **any hostile character, regardless of Size**. Pure predicate over the
 * frozen state — `true` iff `c` is placed and some *placed hostile* is footprint-adjacent
 * (footprint-to-footprint distance exactly 1, via the shared `combatantRange` metric the
 * overwatch/range gates use). Hostility is the activation **side** split (`sideOf`:
 * mech/pilot ⇒ pc, else npc), the one team model the engine has.
 *
 * **No Size gate here — deliberately.** RAW gates only the mid-move *stop* by Size
 * ("characters that become ENGAGED by targets of equal or greater SIZE ... stop moving"); the
 * *status itself* applies unconditionally ("they BOTH gain the ENGAGED status"), so a Size-2
 * mech adjacent to a Size-1 hostile IS Engaged. The Size-gated **stop** check lives separately,
 * as `engagersAt` (`@lancer-vtt/domain`'s `movement.ts`) — keep the two predicates visibly
 * distinct: **Size matters twice, differently.**
 *
 * **The pilot-directionality exemption (RAW p.74).** An
 * unmounted pilot never CAUSES Engaged on a non-pilot — a human on foot doesn't threaten
 * the space a mech occupies the way another mech does. A mech (or NPC) still causes
 * Engaged on a pilot, and pilot-vs-pilot stays ordinary/mutual (the exemption only fires
 * when the CAUSING side is a pilot and the AFFECTED side isn't a pilot) — the exact
 * directionality `classifyOccupant` already uses for movement pass-through
 * (`occupant.kind === "pilot" && mover.kind !== "pilot"`, `@lancer-vtt/domain`'s
 * `movement.ts`). Do NOT touch the Size handling above — this is a wholly separate axis.
 */
function isEngaged(state: EncounterState, c: Combatant): boolean {
  if (!c.pos) return false; // an unplaced unit engages no one and is engaged by no one.
  // Engagement is a character-vs-character rule (RAW Core p.62) — objects/deployables aren't
  // characters (they never enter the activation/combat-status machine), so an object is never
  // Engaged, and a placed object (which folds to the "npc" side via `sideOf`) never engages a
  // character. Same `isActor` exclusion the hide watcher-loop and the activation selectors use —
  // objects contribute to spatial *occupancy*, not engagement.
  //
  // Deliberately STILL `isActor`, not `isCharacter`:
  // a non-acting SIZE-1/2 drone never imposes or holds Engaged (RAW needs equal-or-greater
  // SIZE — the same reason a pilot doesn't), and the status would be inert on a body that
  // never attacks or moves. Contrast `movement.ts`'s `engagersAt`, which DOES admit a drone into
  // its movement-STOP loop (occupancy, a different axis) without granting this status.
  if (!isActor(c)) return false;
  for (const other of Object.values(state.combatants)) {
    if (other.id === c.id) continue;
    if (!isEngagedWith(state, c.id, other.id)) continue;
    if (other.kind === "pilot" && c.kind !== "pilot") continue; // a pilot never causes Engaged on a non-pilot.
    return true;
  }
  return false;
}

/**
 * **Pairwise** engagement — is `a` engaged specifically WITH `b` (footprint-adjacent AND
 * hostile), as opposed to {@link isEngaged}'s "engaged with ANYONE"? The Ordnance
 * engaged-target gate (RAW Core p.105: "can't be used against targets in engagement with the
 * user's mech") needs the PAIRWISE fact — an
 * Ordnance weapon must reject only when the CHOSEN TARGET is the one engaging the attacker,
 * never the `Engaged` STATUS (which only records "engaged with anyone" and would wrongly
 * reject an Ordnance shot at a target across the map just because a THIRD unit happens to be
 * adjacent to the attacker elsewhere). **Positions, not status** — the same hard rule the
 * movement stop-detection follows: always recompute from POSITIONS, never read the possibly-stale
 * `Engaged` status. `isEngaged` above is built ON this pairwise primitive (a sweep over every
 * other combatant), so the two predicates can't silently drift apart.
 */
export function isEngagedWith(state: EncounterState, a: string, b: string): boolean {
  const ca = state.combatants[a];
  const cb = state.combatants[b];
  if (!ca || !cb) return false;
  if (!ca.pos || !cb.pos) return false; // unplaced — no position to be adjacent from.
  if (!isActor(ca) || !isActor(cb)) return false; // objects/deployables AND drones never engage (see `isEngaged`'s note).
  if (hasStatus(ca.statuses, "Destroyed") || hasStatus(cb.statuses, "Destroyed")) return false; // a corpse left on the board threatens no one.
  if (sideOf(ca) === sideOf(cb)) return false; // only a *hostile* engages.
  return combatantRange(state, a, b) === 1; // footprint-adjacent.
}

/**
 * **Engagement adjacency recomputer.** After any unit moves, re-derive the
 * `Engaged` status for **every** placed combatant and emit the minimal `status_add` /
 * `status_remove` steps that bring the stored statuses back in sync with the geometry.
 *
 * **Why a full sweep, not just the mover.** A single move changes the engagement of the
 * mover *and* of every unit it moved next to or away from, and a third unit's engagement
 * can rest on a hostile other than the mover — so the only order-independent, correct
 * recompute is to re-evaluate all placed combatants from scratch. Encounters are small,
 * and the steps are idempotent (`status_add`/`status_remove` no-op when already in the
 * target state), so a recompute that changes nothing emits zero steps (no broadcast).
 *
 * **Also run outside a move**: {@link "./referee/refereeIntent".resyncEngagementInto} re-runs this sweep
 * after a destruction or a GM `remove_combatant`, because a hostile leaving the board un-engages
 * its neighbours without anyone moving.
 *
 * **Scope:** adjacency *detection* only — it lands/clears the canonical RAW `Engaged` status.
 * The *consequences* — the ranged-attack +1 difficulty, the equal-or-greater-Size mid-move stop,
 * Disengage, the free-strike on fleeing — belong to the movement rules that consume this status.
 * **RAW has no movement-halving** for Engaged or Grapple (verified against the core book
 * pp.62/69/77) — a same-Size grapple pre-contest is Engaged (the ranged/stop riders apply) but
 * drags/Immobilizes no one until the contest resolves.
 */
export const engagementRecomputer: SpatialRecomputer = {
  id: "engagement",
  recompute({ state }: MoveContext): ResolvedStep[] {
    const steps: ResolvedStep[] = [];
    // Sort ids so the emitted step order is deterministic (replay byte-identity), never
    // dependent on `combatants` insertion order.
    for (const id of Object.keys(state.combatants).sort()) {
      const c = state.combatants[id];
      if (c === undefined) continue;
      const engaged = isEngaged(state, c);
      const has = c.statuses.some((s) => s.name === ENGAGED_STATUS);
      if (engaged && !has) {
        steps.push({ op: "status_add", target: id, status: ENGAGED_STATUS, expiry: { kind: "until_removed" } });
      } else if (!engaged && has) {
        steps.push({ op: "status_remove", target: id, status: ENGAGED_STATUS });
      }
    }
    return steps;
  },
};

/**
 * **Grapple broken-adjacency recomputer** — mirrors {@link engagementRecomputer}: after any unit
 * moves, re-check every `Grappled`-carrying
 * combatant against its bound partner and clear the marker on both when they're no longer
 * footprint-adjacent. This is the honest release valve for the deferred mirror-movement drag —
 * the grapplee's Immobilized is advisory only, so it CAN walk away, and without this hook
 * it would stay "Grappled" forever (stale state). RAW ending condition: "either character
 * breaks adjacency."
 *
 * **Why a full sweep, not just the mover.** Either side of a bound pair moving away breaks the
 * grapple, so both must be checked, not just `ctx.moved` — mirrors `engagementRecomputer`'s
 * same reasoning. Each Grappled party's own marker is evaluated (and cleared) independently —
 * **not** via {@link grappleClearSteps} (a paired clear for a single caller) — so the sweep
 * never double-emits a `status_remove` for the same pair (id A's iteration clears only A's own
 * marker; B's marker clears on B's own iteration). Idempotent (a pair still adjacent, or
 * already ungrappled, emits nothing).
 *
 * This chain ALSO fires on `place_token` and on a resolution's own `forced_move` steps
 * (`backend/src/encounterRoom.ts`'s `applyPlace` / `stepsMovePosition` followup), so a broken
 * grapple re-syncs on a GM relocation or a Knockback too, not just `move_token`.
 */
export const grappleRecomputer: SpatialRecomputer = {
  id: "grapple",
  recompute({ state }: MoveContext): ResolvedStep[] {
    const steps: ResolvedStep[] = [];
    for (const id of Object.keys(state.combatants).sort()) {
      const c = state.combatants[id];
      if (c === undefined) continue;
      const g = c.statuses.find((s) => s.name === GRAPPLED_STATUS);
      if (g === undefined || g.partner === undefined) continue;
      if (combatantRange(state, id, g.partner) === 1) continue; // still adjacent — grapple holds.
      steps.push(statusRemoveStep(id, g));
    }
    return steps;
  },
};

/**
 * **Grapple dominance/Immobilized sync recomputer** (audit label
 * `spatial_grapple_dominance`) — after any unit moves, re-run the full-sweep
 * {@link grappleImmobilizedSyncSteps}
 * sync so the smaller/non-dominant party's partner-provenance `Immobilized` (and any orphaned
 * `Grapple Dominance` marker) stay in sync with the pair's current dominance. Runs **after**
 * {@link grappleRecomputer} in the chain so an adjacency-ended grapple's markers clear in the
 * **same** `applyMove` chain — `grappleRecomputer` has already removed the stale `Grappled`
 * marker by the time this recomputer's sweep reads the post-move state, so the walked-away party
 * reads as no-longer-grappled and its Immobilized/Dominance clear as orphaned.
 */
export const grappleDominanceSyncRecomputer: SpatialRecomputer = {
  id: "grapple-dominance-sync",
  recompute({ state }: MoveContext): ResolvedStep[] {
    return grappleImmobilizedSyncSteps(state);
  },
};

/**
 * The status name Argonaut Shield's Quick action plants on the ally it covers
 * (`ms_argonaut_shield`, core IPS-N Drake LL1 — `seedEntries.ts`'s `ARGONAUT_SHIELD` overlay
 * entry). Its `partner` points at the Drake.
 */
export const ARGONAUT_SHIELD_STATUS = "Argonaut Shield";

/**
 * **Argonaut Shield adjacency-break recomputer** — mirrors
 * {@link grappleRecomputer} exactly: after any unit moves, re-check every `Argonaut Shield`-
 * carrying combatant against its bound partner (the Drake) and clear the marker when they're no
 * longer footprint-adjacent. RAW: "this effect lasts until your target breaks adjacency, at which
 * point this effect ceases until you repeat this action" — clearing the status is the honest
 * release valve (re-issuing the Quick action re-plants it). Like
 * `grappleRecomputer`/`engagementRecomputer`, this chain also fires on `place_token` and a
 * resolution's own `forced_move` steps, not `move_token` alone.
 */
export const argonautShieldRecomputer: SpatialRecomputer = {
  id: "argonaut-shield",
  recompute({ state }: MoveContext): ResolvedStep[] {
    const steps: ResolvedStep[] = [];
    for (const id of Object.keys(state.combatants).sort()) {
      const c = state.combatants[id];
      if (c === undefined) continue;
      const g = c.statuses.find((s) => s.name === ARGONAUT_SHIELD_STATUS);
      if (g === undefined || g.partner === undefined) continue;
      if (combatantRange(state, id, g.partner) === 1) continue; // still adjacent — the shield holds.
      steps.push({ op: "status_remove", target: id, status: ARGONAUT_SHIELD_STATUS });
    }
    return steps;
  },
};

/**
 * The co-location marker **family** this recomputer keeps in sync — every rider/carrier
 * pair that shares a cell and wants the identical drag/auto-detach machinery. Jockey's
 * `Jockeying`/`Jockeyed` (hostile attach) and allied-riding's `Riding`/`Ridden` (consensual
 * attach) are distinct pairs reached by
 * distinct intents, but both want the SAME two behaviors below — a new pair joins this
 * list (plus its own `detach` step-builder), not a new recomputer.
 */
const COLOCATION_PAIRS: readonly {
  riderStatus: string;
  carrierStatus: string;
  detach: (state: EncounterState, riderId: string, mechId: string) => ResolvedStep[];
}[] = [
  { riderStatus: JOCKEYING_STATUS, carrierStatus: JOCKEYED_STATUS, detach: jockeyDetachSteps },
  { riderStatus: RIDING_STATUS, carrierStatus: RIDDEN_STATUS, detach: rideDetachSteps },
  // A rodeo rider never moves itself (it is not in `order`), so this detach is only a GM drag; it
  // ends the rodeo without the forced-end lockout.
  { riderStatus: RODEO_RIDER_STATUS, carrierStatus: RODEO_STATUS, detach: (state, riderId) => endRodeoSteps(state, riderId) },
];

/**
 * **Co-location rider-tracking recomputer** — after any unit moves, keep ANY co-location pair
 * ({@link COLOCATION_PAIRS}) in sync, in EITHER direction:
 *
 *   - **The carrier moved** (a `Jockeyed`/`Ridden` mech is `ctx.moved`): drag its rider along
 *     by **assignment** — a plain `move` step, never a `move_token` — so the rider "shares
 *     its space and moves with it" (RAW). **The step kind is load-bearing**: the rider's
 *     reposition rides the SAME `apply_resolved` this recomputer's own steps land through, so
 *     it never re-enters `applyMove`/`MOVE_LISTENER_RECOMPUTERS`/`SETTLE_RECOMPUTERS` a second time for the rider (only
 *     a real `move_token` does that) — the stop/occupancy classification never evaluates the
 *     shared-space entry, by construction.
 *   - **The rider moved** (a `Jockeying`/`Riding` pilot is `ctx.moved`): that can only be
 *     the rider's OWN `move_token` — a voluntary jump-off ("you can jump off as part of
 *     your movement", RAW). If it's no longer sharing its carrier's cell after that move,
 *     the ride ends: the matching pair's `detach` (`jockeyDetachSteps`/`rideDetachSteps`)
 *     clears the CORRECT pair's markers — never the other pair's step-builder.
 *
 * Placed FIRST in {@link MOVE_LISTENER_RECOMPUTERS} (unlike `grappleRecomputer`'s adjacency-only
 * concern) so that when the carrier moves, the rider's reassignment lands before
 * `engagementRecomputer` sweeps — engagement is computed off the rider's POST-move
 * position within the same move event, not last-move-stale.
 *
 * A single move names exactly one mover, and a combatant carries at most one
 * co-location marker, so at most one branch of one pair fires per call. Kept the id
 * `"jockey"` (not renamed) — it's a stable identity other code and tests key on
 * (`spatialRecompute.test.ts`'s ordering assertions, `MOVE_LISTENER_RECOMPUTERS`/`SETTLE_RECOMPUTERS` position),
 * unrelated to the `kind: "jockey"` audit label `encounterRoom.ts` stamps on the Jockey
 * INTENT's own resolution (a different, intent-specific label this recomputer doesn't touch).
 */
export const jockeyRecomputer: SpatialRecomputer = {
  id: "jockey",
  recompute({ state, moved }: MoveContext): ResolvedStep[] {
    const mover = state.combatants[moved];
    if (mover === undefined) return [];

    for (const pair of COLOCATION_PAIRS) {
      const carrierMark = mover.statuses.find((s) => s.name === pair.carrierStatus);
      if (carrierMark?.partner === undefined) continue;
      const rider = state.combatants[carrierMark.partner];
      if (rider?.pos && mover.pos && combatantRange(state, rider.id, mover.id) !== 0) {
        return [{ op: "move", target: rider.id, from: rider.pos, to: mover.pos }];
      }
      return [];
    }

    for (const pair of COLOCATION_PAIRS) {
      const riderMark = mover.statuses.find((s) => s.name === pair.riderStatus);
      if (riderMark?.partner === undefined) continue;
      const carrier = state.combatants[riderMark.partner];
      const stillTogether = carrier?.pos != null && mover.pos != null && combatantRange(state, mover.id, carrier.id) === 0;
      if (!stillTogether) return pair.detach(state, mover.id, riderMark.partner);
      return [];
    }

    return [];
  },
};

/**
 * **Post-move Hidden-reveal recomputer** — after any unit moves, re-check
 * every Hidden combatant's cover/LoS and clear Hidden on whichever units the move has now
 * exposed (a hostile crested a wall, or stepped into a clear line it didn't have before). Body is
 * the shared {@link revealExposedHiddenSteps} sweep (`hide.ts`) — the SAME one `coverDestroyedObserver`
 * (`attackObservers.ts`) emits at its own post-attack trigger point; the two share a sweep body but
 * keep distinct audit labels (this recomputer's steps are auto-stamped `spatial_hidden_reveal` by
 * the room, vs. the post-attack `reveal_hidden` — a distinct seam earns its own audit trail).
 * Ignores `moved`/`from` deliberately — a full sweep, not a
 * mover-only check: a move can change the LoS a Hidden unit has from ANY hostile, and the mover
 * may be the hider itself (a hider that walks out of its own cover reveals — RAW-correct).
 */
export const hiddenRevealRecomputer: SpatialRecomputer = {
  id: "hidden_reveal",
  recompute({ state }: MoveContext): ResolvedStep[] {
    return revealExposedHiddenSteps(state);
  },
};

/**
 * **What a move sets off, split by D15** (`event-engine-lifecycle` 5.3, the foreman's ruling A): the
 * line falls where RAW names a consequence.
 *
 * {@link MOVE_LISTENER_RECOMPUTERS} are core-rule listeners on `moved`, in this order: the grapple
 * drag (a move a character makes, Core p.62), the jockey/ride carry and detach, a grapple broken by
 * adjacency and an Argonaut Shield broken by adjacency (Core p.69), and Hidden lost on exposure
 * (Core p.69). **`dragRecomputer` and `jockeyRecomputer` run first**: the later ones read the
 * partner's post-drag and the rider's post-jockey position, so a grapple ends only when the drag
 * genuinely couldn't hold adjacency. **`hiddenRevealRecomputer` runs last**: it reads cover/LoS
 * geometry, so it must see every final position.
 *
 * {@link SETTLE_RECOMPUTERS} are derived state, re-synced in a settle pass after the listeners (and
 * after a destroy or a `remove_combatant`): Engaged restates adjacency, and the grapple
 * dominance/Immobilized sync restates the pair's Sizes. Nothing listens to them and nothing replaces
 * them. Engagement reads the rider's post-jockey position because the settle pass runs after the
 * listeners (`observerOrder.characterization.test.ts`).
 *
 * Each list's order is fixed (never registration order), and each recomputer emits its steps in
 * sorted id order, so the recorded step list is byte-identical across replay.
 */
export const MOVE_LISTENER_RECOMPUTERS: readonly SpatialRecomputer[] = [dragRecomputer, jockeyRecomputer, grappleRecomputer, argonautShieldRecomputer, hiddenRevealRecomputer];

/** The derived state a move, a destroy or a removal re-syncs ({@link MOVE_LISTENER_RECOMPUTERS}' doc). */
export const SETTLE_RECOMPUTERS: readonly SpatialRecomputer[] = [engagementRecomputer, grappleDominanceSyncRecomputer];
